/**
 * IntegrationsPage — Phase 2 Redesign
 *
 * Card layout replaces the table:
 *  - Each card shows company name, code, user count, 4 integration status dots, overall health chip
 *  - Active/inactive dimming inline (no separate filter needed)
 *  - Sheet opens with single Save button + dirty-state guard
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Building2,
  Plus,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Search,
  Users,
  PoundSterling,
  FolderKanban,
  CalendarDays,
  Circle,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { PageHeader } from '@/components/blocks/page-header';
import { CompanySheet } from '@/components/companies/CompanySheet';
import * as integrationsService from '@/services/integrationsRedesignService';
import { getAccessProjectsCompanies, getAccessPlannerCompanies } from '@/services/integrationsApi';
import { mockIntegrationsService } from '@/data/mockIntegrationsData';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { usePermissions } from '@/contexts/PermissionContext';
import { AccessRestricted } from '@/components/shared/AccessRestricted';
import type {
  CompanyWithIntegrations,
  IntegrationConnectionStatus,
  FinancialProduct,
  HRProduct,
  ProjectProduct,
  PlannerProduct,
} from '@/types';
import {
  FINANCIAL_PRODUCT_LABELS,
  HR_PRODUCT_LABELS,
  PROJECT_PRODUCT_LABELS,
  PLANNER_PRODUCT_LABELS,
} from '@/types/integrations-redesign';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Resolves a raw product slug to a human-readable label */
function getProductLabel(product: string): string {
  return (
    FINANCIAL_PRODUCT_LABELS[product as FinancialProduct] ??
    HR_PRODUCT_LABELS[product as HRProduct] ??
    PROJECT_PRODUCT_LABELS[product as ProjectProduct] ??
    PLANNER_PRODUCT_LABELS[product as PlannerProduct] ??
    product // fallback: show as-is if unknown
  );
}

/** Returns overall health bucket for a company */
function getCompanyHealth(
  company: CompanyWithIntegrations
): 'all-connected' | 'has-errors' | 'not-connected' | 'not-configured' {
  const statuses: IntegrationConnectionStatus[] = [
    company.financialIntegration.status,
    company.hrIntegration.status,
    company.projectIntegration.status,
    company.plannerIntegration.status,
  ];
  const configured = statuses.filter((s) => s !== 'not_configured');
  if (configured.length === 0) return 'not-configured';
  if (configured.some((s) => s === 'error')) return 'has-errors';
  if (configured.some((s) => s === 'not_connected')) return 'not-connected';
  return 'all-connected';
}

// ---------------------------------------------------------------------------
// Status dot (inline)
// ---------------------------------------------------------------------------

function StatusDot({ status }: { status: IntegrationConnectionStatus }) {
  if (status === 'connected')
    return <span className="h-2 w-2 rounded-full bg-success-500 flex-shrink-0 ring-2 ring-success-500/20" />;
  if (status === 'error')
    return <span className="h-2 w-2 rounded-full bg-destructive flex-shrink-0 ring-2 ring-destructive/20" />;
  if (status === 'not_connected')
    return <span className="h-2 w-2 rounded-full bg-warning-500 flex-shrink-0 ring-2 ring-warning-500/20" />;
  return <span className="h-2 w-2 rounded-full bg-muted-foreground/30 flex-shrink-0 ring-2 ring-muted/40" />;
}

// ---------------------------------------------------------------------------
// Integration pill — icon + status dot + product name (or "not set" hint)
// ---------------------------------------------------------------------------

/** Products where the display name comes from externalInstanceIdSecondary */
const SECONDARY_ID_PRODUCTS = new Set(['access-projects', 'access-planner']);

function IntegrationPill({
  icon,
  label,
  product,
  status,
  instanceName,
  secondaryId,
  secondaryIdNames,
}: {
  icon: React.ReactNode;
  label: string;
  product: string | null;
  status: IntegrationConnectionStatus;
  instanceName?: string | null;
  secondaryId?: string | null;
  secondaryIdNames?: Record<string, string>;
}) {
  let displayName: string | null = null;
  if (product && SECONDARY_ID_PRODUCTS.has(product) && secondaryId) {
    displayName = secondaryIdNames?.[secondaryId] ?? secondaryId;
  } else {
    displayName = instanceName || null;
  }

  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <span className="text-muted-foreground/60 flex-shrink-0">{icon}</span>
      {product ? (
        <>
          <StatusDot status={status} />
          <span className="text-xs font-medium truncate">
            {displayName || getProductLabel(product)}
          </span>
        </>
      ) : (
        <span className="text-xs text-muted-foreground/45 italic">{label} not set</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Health indicator pill
// ---------------------------------------------------------------------------

function HealthIndicator({ health }: { health: ReturnType<typeof getCompanyHealth> }) {
  switch (health) {
    case 'all-connected':
      return (
        <div className="flex items-center gap-1 text-success-700 bg-success-50 border border-success-200 rounded-full px-2 py-0.5">
          <CheckCircle2 className="h-3 w-3" />
          <span className="text-[10px] font-semibold">All Connected</span>
        </div>
      );
    case 'has-errors':
      return (
        <div className="flex items-center gap-1 text-destructive bg-destructive/5 border border-destructive/20 rounded-full px-2 py-0.5">
          <XCircle className="h-3 w-3" />
          <span className="text-[10px] font-semibold">Has Errors</span>
        </div>
      );
    case 'not-connected':
      return (
        <div className="flex items-center gap-1 text-warning-700 bg-warning-50 border border-warning-200 rounded-full px-2 py-0.5">
          <AlertCircle className="h-3 w-3" />
          <span className="text-[10px] font-semibold">Not Connected</span>
        </div>
      );
    case 'not-configured':
      return (
        <div className="flex items-center gap-1 text-muted-foreground bg-muted/60 border rounded-full px-2 py-0.5">
          <Circle className="h-3 w-3" />
          <span className="text-[10px] font-semibold">Not Configured</span>
        </div>
      );
  }
}

// ---------------------------------------------------------------------------
// Company card — full-width list style matching mockup
// ---------------------------------------------------------------------------

interface CompanyCardProps {
  company: CompanyWithIntegrations;
  isSelected: boolean;
  onClick: () => void;
  secondaryIdNames?: Record<string, string>;
}

function CompanyCard({ company, isSelected, onClick, secondaryIdNames }: CompanyCardProps) {
  const health = getCompanyHealth(company);
  const isInactive = company.status === 'inactive';
  // Derive 2-letter avatar from company code
  const initials = company.code.slice(0, 2).toUpperCase();

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left rounded-lg border bg-card p-4 transition-all duration-150 hover:border-primary/30 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
        isSelected && 'border-primary/60 bg-primary/5 shadow-sm',
        isInactive && 'opacity-60'
      )}
    >
      {/* ── Top row: avatar + identity + health + chevron ── */}
      <div className="flex items-start gap-3 min-w-0">
        {/* Code-initial avatar */}
        <div className={cn(
          'flex h-9 w-9 items-center justify-center rounded-md flex-shrink-0 text-sm font-bold',
          isInactive ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'
        )}>
          {initials}
        </div>

        {/* Name + code + user count */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm leading-tight">{company.name}</span>
            {isInactive && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Inactive</Badge>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-muted-foreground font-mono">{company.code}</span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="h-3 w-3" />
              {company.userCount} {company.userCount === 1 ? 'user' : 'users'}
            </span>
          </div>
        </div>

        {/* Health + chevron */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <HealthIndicator health={health} />
          <ChevronRight className={cn(
            'h-4 w-4 text-muted-foreground transition-transform duration-150',
            isSelected && 'rotate-90 text-primary'
          )} />
        </div>
      </div>

      {/* ── Integration pills: 2×2 grid indented under avatar ── */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 mt-3 pl-12">
        <IntegrationPill
          icon={<PoundSterling className="h-3 w-3" />}
          label="Financial"
          product={company.financialIntegration.product}
          status={company.financialIntegration.status}
          instanceName={company.financialIntegration.instanceName}
        />
        <IntegrationPill
          icon={<Users className="h-3 w-3" />}
          label="HR"
          product={company.hrIntegration.product}
          status={company.hrIntegration.status}
          instanceName={company.hrIntegration.instanceName}
        />
        <IntegrationPill
          icon={<FolderKanban className="h-3 w-3" />}
          label="Project"
          product={company.projectIntegration.product}
          status={company.projectIntegration.status}
          instanceName={company.projectIntegration.instanceName}
          secondaryId={company.projectIntegration.externalInstanceIdSecondary}
          secondaryIdNames={secondaryIdNames}
        />
        <IntegrationPill
          icon={<CalendarDays className="h-3 w-3" />}
          label="Scheduler"
          product={company.plannerIntegration.product}
          status={company.plannerIntegration.status}
          instanceName={company.plannerIntegration.instanceName}
          secondaryId={company.plannerIntegration.externalInstanceIdSecondary}
          secondaryIdNames={secondaryIdNames}
        />
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Card loading skeleton
// ---------------------------------------------------------------------------

function CardSkeleton() {
  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-start gap-3">
        <Skeleton className="h-9 w-9 rounded-md flex-shrink-0" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="h-5 w-28 rounded-full" />
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 pl-12">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-3.5 w-32 rounded" />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

interface IntegrationsPageProps {
  hideHeader?: boolean;
}

const IntegrationsPage = ({ hideHeader = false }: IntegrationsPageProps) => {
  const { isDemo } = useScreenDataMode('integrations');
  const { canViewIntegrations, canManageIntegrations } = usePermissions();
  const [companies, setCompanies] = useState<CompanyWithIntegrations[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'active' | 'inactive'>('active');
  const [selectedCompany, setSelectedCompany] = useState<CompanyWithIntegrations | null>(null);
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [secondaryIdNames, setSecondaryIdNames] = useState<Record<string, string>>({});

  const loadCompanies = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = isDemo
        ? await mockIntegrationsService.getCompanies()
        : await integrationsService.getCompanies();
      const sorted = [...data].sort((a, b) => {
        if (a.status === b.status) return a.name.localeCompare(b.name);
        return a.status === 'active' ? -1 : 1;
      });
      setCompanies(sorted);

      // Resolve externalInstanceIdSecondary → display names for access-projects/access-planner
      const nameMap: Record<string, string> = {};
      const seen = new Set<string>();
      const lookups: Array<{ externalInstanceId: string; product: string }> = [];
      for (const c of sorted) {
        for (const cfg of [c.projectIntegration, c.plannerIntegration]) {
          if (
            cfg.product &&
            SECONDARY_ID_PRODUCTS.has(cfg.product) &&
            cfg.externalInstanceIdSecondary &&
            cfg.externalInstanceId &&
            !seen.has(cfg.externalInstanceId)
          ) {
            seen.add(cfg.externalInstanceId);
            lookups.push({ externalInstanceId: cfg.externalInstanceId, product: cfg.product });
          }
        }
      }
      await Promise.all(
        lookups.map(async ({ externalInstanceId, product }) => {
          try {
            const companies = product === 'access-planner'
              ? await getAccessPlannerCompanies(externalInstanceId)
              : await getAccessProjectsCompanies(externalInstanceId);
            for (const co of companies) {
              nameMap[co.id] = co.name;
            }
          } catch {
            // Silently skip — fallback to raw ID in IntegrationPill
          }
        })
      );
      setSecondaryIdNames(nameMap);
    } catch (error) {
      console.error('Failed to load companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setIsLoading(false);
    }
  }, [isDemo]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const filteredCompanies = useMemo(() => {
    return companies.filter((company) => {
      const matchesSearch =
        company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        company.code.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus =
        selectedStatus === 'all' || company.status === selectedStatus;
      return matchesSearch && matchesStatus;
    });
  }, [companies, searchQuery, selectedStatus]);

  async function handleCompanyCreated(created: CompanyWithIntegrations) {
    await loadCompanies();
    setShowCreateSheet(false);
    setSelectedCompany(created);
  }

  function handleSheetCompanyUpdate(updated: CompanyWithIntegrations) {
    setCompanies((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    setSelectedCompany(updated);
  }

  if (!canViewIntegrations()) {
    return (
      <div className="space-y-6">
        <PageHeader title="Integrations" size={36} showBreadcrumb={false} icon={<Building2 />} />
        <AccessRestricted message="You don't have permission to view Integrations. Contact your administrator to request access." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {hideHeader ? (
        canManageIntegrations() && (
          <div className="flex justify-end">
            <Button onClick={() => setShowCreateSheet(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Company
            </Button>
          </div>
        )
      ) : (
        <PageHeader
          title="Integrations"
          size={36}
          showBreadcrumb={false}
          icon={<Building2 />}
          actions={
            canManageIntegrations() && (
              <Button onClick={() => setShowCreateSheet(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Company
              </Button>
            )
          }
        />
      )}

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search companies…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={selectedStatus} onValueChange={(v) => setSelectedStatus(v as 'all' | 'active' | 'inactive')}>
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary bar */}
      {!isLoading && companies.length > 0 && (
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>{filteredCompanies.length} of {companies.length} companies</span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-success-500 inline-block" />
            {companies.filter((c) => getCompanyHealth(c) === 'all-connected').length} fully connected
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-destructive inline-block" />
            {companies.filter((c) => getCompanyHealth(c) === 'has-errors').length} with errors
          </span>
        </div>
      )}

      {/* Card list */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
          <Building2 className="h-12 w-12 text-muted-foreground/30" />
          <p className="font-medium text-muted-foreground">No companies found</p>
          {searchQuery && (
            <button className="text-xs text-primary underline" onClick={() => setSearchQuery('')}>
              Clear search
            </button>
          )}
          {canManageIntegrations() && !searchQuery && (
            <Button size="sm" onClick={() => setShowCreateSheet(true)}>
              <Plus className="mr-2 h-3.5 w-3.5" /> Add Company
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filteredCompanies.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              isSelected={selectedCompany?.id === company.id}
              onClick={() => setSelectedCompany(company)}
              secondaryIdNames={secondaryIdNames}
            />
          ))}
        </div>
      )}

      {/* Detail sheet */}
      <CompanySheet
        company={selectedCompany}
        open={!!selectedCompany}
        onOpenChange={(open) => { if (!open) setSelectedCompany(null); }}
        onCompanyUpdate={handleSheetCompanyUpdate}
        readOnly={!canManageIntegrations()}
      />

      {/* Add Company sheet */}
      <CompanySheet
        company={null}
        open={showCreateSheet}
        onOpenChange={setShowCreateSheet}
        onCompanyUpdate={() => {}}
        mode="create"
        onCompanyCreated={handleCompanyCreated}
        existingCodes={companies.map((c) => c.code.toUpperCase())}
      />
    </div>
  );
};

export default IntegrationsPage;
